import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';

import { UtilsService } from '../../services/helpers/utils.service';
import { ModalErrorComponent } from '../../sections/modal-error/modal-error.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { IFileInfo, Dictionary } from '../../models/models';



export interface OrganizationTreeElement {
  id: number;
  name: string;
  children: OrganizationTreeElement[];
  parent?: OrganizationTreeElement;
}
export interface OrganizationTreeNode {
  id: number;
  expandable: boolean;
  name: string;
  level: number;
}

@Component({
  selector: 'app-org-dep-div-selector',
  templateUrl: './org-dep-div-selector.component.html',
})
export class OrgDepDivSelectorComponent implements OnInit, OnChanges {
  
  @Input() headerText: string;
  @Input() organizationHeaderText: string;
  @Input() departmentHeaderText: string;
  @Input() divisionHeaderText: string;
  @Input() userBlockHeaderText: string;

  @Input() allOrganizations: Dictionary[] = [];
  @Input() allDepartments: Dictionary[] = [];
  @Input() allDivisions: Dictionary[] = [];
  @Input() allUserBlocks: Dictionary[] = [];

  filteredOrganizations: Dictionary[] = [];
  filteredDepartments: Dictionary[] = [];
  filteredDivisions: Dictionary[] = [];
  filteredUserBlocks: Dictionary[] = [];
  parentOfSelectedUserBlocks: Dictionary[] = [];

  @Input() selectedOrganizations: Dictionary[];
  @Output() selectedOrganizationsChange = new EventEmitter<Dictionary[]>();
  @Input() selectedDepartments: Dictionary[];
  @Output() selectedDepartmentsChange = new EventEmitter<Dictionary[]>();
  @Input() selectedDivisions: Dictionary[];
  @Output() selectedDivisionsChange = new EventEmitter<Dictionary[]>();
  @Input() selectedUserBlocks: Dictionary[];
  @Output() selectedUserBlocksChange = new EventEmitter<Dictionary[]>();

  
  treeControl = new FlatTreeControl<OrganizationTreeNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this.transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);


  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["allOrganizations"] ||
      changes["allDepartments"] ||
      changes["allDivisions"] ||
      changes["allUserBlocks"] ||
      changes["selectedOrganizations"] ||
      changes["selectedDepartments"] ||
      changes["selectedDivisions"])
    {
      this._evaluateChanges();
    }
  }

  private _evaluateChanges() {
    this.filteredOrganizations = this.allOrganizations;

    if (this.selectedOrganizations?.length && this.allDepartments?.length) {
      this.filteredDepartments = this.allDepartments.filter((dep) => this.selectedOrganizations.some(org => org.value == dep.parentValue));
    }
    else {
      this.filteredDepartments = [];
    }


    if (this.selectedDepartments?.length && this.allDivisions?.length) {
      this.filteredDivisions = this.allDivisions.filter((div) => this.selectedDepartments.some(dep => dep.value == div.parentValue));
    }
    else {
      this.filteredDivisions = [];
    }


    if (this.selectedDivisions?.length && this.allUserBlocks?.length)
    {
      const parentUserBlocks = this.allUserBlocks.filter((userBlock) => this.selectedDivisions.some(div => userBlock.code == div.code));

      let data = this.allUserBlocks
        .filter((item) => parentUserBlocks.some(parent => parent.value == item.parentValue))
        .map((element) => {
          return {
            ...element,
            parentValue: 0,
          };
        });

      for (let i = 0; i < data?.length; i++) {
        const res = this.allUserBlocks.filter((x) => x.parentValue === data[i].value) as Dictionary[];
        if (res?.length) {
          data = data.concat(res);
        }
      }

      const dataTree: OrganizationTreeElement[] = this.createDataTree(
        data.map((item) => {
          return {
            id: item.value,
            name: item.name,
            parentId: item.parentValue,
          };
        })
      );

      this.filteredUserBlocks = data;
      this.dataSource.data = dataTree;
    }
    else {
      this.dataSource.data = [];
    }
  }

  public organizationChangeEvent(event: Event) {
    const organizationId = +(event.target as HTMLSelectElement).value;
    if (!isNaN(organizationId)) {
      this.selectedOrganizations = this.allOrganizations.filter(q => q.value == organizationId);
    }
    else {
      this.selectedOrganizations = [];
    }

    this.selectedOrganizationsChange.emit(this.selectedOrganizations);
    this.selectedDepartments = [];
    this.selectedDepartmentsChange.emit(this.selectedDepartments);
    this.selectedDivisions = [];
    this.selectedDivisionsChange.emit(this.selectedDivisions);
    this.selectedUserBlocks = [];
    this.selectedUserBlocksChange.emit(this.selectedUserBlocks);
    this._evaluateChanges();
  }

  public departmentChangeEvent(event: Event) {
    const departmentId = +(event.target as HTMLSelectElement).value;
    if (!isNaN(departmentId)) {
      this.selectedDepartments = this.allDepartments.filter(q => q.value == departmentId);
    }
    else {
      this.selectedDepartments = [];
    }

    this.selectedDepartmentsChange.emit(this.selectedDepartments);
    this.selectedDivisions = [];
    this.selectedDivisionsChange.emit(this.selectedDivisions);
    this.selectedUserBlocks = [];
    this.selectedUserBlocksChange.emit(this.selectedUserBlocks);
    this._evaluateChanges();
  }

  public divisionChangeEvent(event: Event) {
    const divisionId = +(event.target as HTMLSelectElement).value;
    if (!isNaN(divisionId)) {
      this.selectedDivisions = this.allDivisions.filter(q => q.value == divisionId);
    }
    else {
      this.selectedDivisions = [];
    }

    this.selectedDivisionsChange.emit(this.selectedDivisions);
    this.selectedUserBlocks = [];
    this.selectedUserBlocksChange.emit(this.selectedUserBlocks);
    this._evaluateChanges();
  }

  public userBlockChangeEvent(event: MatCheckboxChange, node: OrganizationTreeNode) {
    this.parentOfSelectedUserBlocks = [];
    if (event.checked) {
      // выбирает всех кроме родителя
      // let data: Dictionary[] = []
      // data = this.childsTree!.filter((x) => x.value === id);
      // for (let e = 0; e < data.length; e++) {
      //   const res = this.childsTree.find((x) => x.value === data[e].parentValue) as Dictionary;
      //   const resNext = this.childsTree.find((x) => x.value === res?.parentValue) as Dictionary;
      //   if (res && resNext) {
      //     data = data.concat(res);
      //   } else {
      //     this.parent = this.parent.concat(res);
      //   }
      // }
      // this.userBlocks = data;

      // выбирает только выбранный узел
      let data: Dictionary[] = [];
      data = this.filteredUserBlocks!.filter((x) => x.value === node.id);

      let i = 0;
      let isNext = this.filteredUserBlocks.some((x) => x.value === data[i]?.parentValue);
      while (isNext) {
        const res =
          i === 0
            ? (this.filteredUserBlocks.find((x) => x.value === data[i].parentValue) as Dictionary)
            : (this.filteredUserBlocks.find((x) => x.value === this.parentOfSelectedUserBlocks[i - 1].parentValue) as Dictionary);
        if (res) {
          this.parentOfSelectedUserBlocks = this.parentOfSelectedUserBlocks.concat(res);
        }
        isNext = this.filteredUserBlocks.some((x) => x.value === this.parentOfSelectedUserBlocks[i]?.parentValue);
        i += 1;
      }
      this.selectedUserBlocks = data;
    }
    else {
      this.selectedUserBlocks = [];
    }

    this.selectedUserBlocksChange.emit(this.selectedUserBlocks);
  }

  
  public isOrganizationSelected(item: Dictionary): boolean {
    return this.selectedOrganizations?.some(q => q.value == item.value);
  }

  public  isDepartmentSelected(item: Dictionary): boolean {
    return this.selectedDepartments?.some(q => q.value == item.value);
  }

  public isDivisionSelected(item: Dictionary): boolean {
    return this.selectedDivisions?.some(q => q.value == item.value);
  }

  public isUserBlockSelected(id: number): boolean {
    return this.selectedUserBlocks?.some((userBlock) => userBlock?.value === id);
  }

  public isParentOfSelectedUserBlock(id: number): boolean {
    return this.parentOfSelectedUserBlocks?.some((item) => item?.value === id);
  }
  
  public userBlockHasChild(_: number, node: OrganizationTreeNode) {
    return node.expandable;
  }

  public getSelectedUserBlocksDisplayText(): string {
    var selectedUserBlock = this.selectedUserBlocks[0];
    if (!selectedUserBlock)
      return 'Все';

    //https://dev.azure.com/tekora/4I/_workitems/edit/7378/
    //Не отображаем подразделение ниже 3 уровня, так как
    //при определенных настройках туда пишется автоматом при сохранении Org, Dep или Div
    let level = this.getUserBlockLevel(selectedUserBlock.value);
    if (level < 3)
      return 'Все';

    return selectedUserBlock.name;
  }

  private getUserBlockLevel(id: number): number {
    let level: number = 0;
    let currentItem : Dictionary = this.allUserBlocks.filter(q => q.value == id)[0];
    while (currentItem && currentItem.parentValue != null && currentItem.parentValue != 0) {
      level++;
      currentItem = this.allUserBlocks.filter(q => q.value == currentItem.parentValue)[0];
    }
    return level;
  }

  private transformer(node: OrganizationTreeElement, level: number) {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level,
      id: node.id,
    }
  }

  private createDataTree(dataset: any) {
    const hashTable = Object.create(null);
    // eslint-disable-next-line no-return-assign
    dataset.forEach(
      (aData: { id: string | number }) => (hashTable[aData.id] = { ...aData, children: [] })
    );
    const dataTree: any[] = [];
    dataset.forEach((aData: { parentId: string | number; id: string | number }) => {
      if (aData.parentId) hashTable[aData.parentId].children.push(hashTable[aData.id]);
      else dataTree.push(hashTable[aData.id]);
    });
    return dataTree;
  };

  private expandWithParents(directoryUserBlockIds: number[]): boolean {
    const result = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const directoryUserBlockId of directoryUserBlockIds) {
      const node = this.getNodeById(this.treeControl.dataNodes, directoryUserBlockId);
      if (node != null) {
        this.expandParents(node);
      }
    }
    return result;
  }

  private getNodeById(
    nodes: OrganizationTreeNode[],
    directoryUserBlockId: number
  ): OrganizationTreeNode | null {
    let result: OrganizationTreeNode | null = null;
    if (Array.isArray(nodes) && nodes.length > 0) {
      let i = 0;

      do {
        result = nodes[i];
      } while (result.id !== directoryUserBlockId && i++ < nodes.length);

      if (i >= nodes.length) {
        result = null;
      }
    }
    return result;
  }

  private expandParents(node: OrganizationTreeNode): void {
    if (node.level > 0) {
      const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
      let i = startIndex;
      let found = false;
      while (i >= 0 && this.treeControl.dataNodes[i].level <= node.level && !found) {
        if (this.treeControl.dataNodes[i].level === node.level - 1) {
          const child = this.getNodeById(
            this.treeControl.getDescendants(this.treeControl.dataNodes[i]),
            node.id
          );
          if (child != null) {
            found = true;
            this.treeControl.expand(this.treeControl.dataNodes[i]);
            this.expandParents(this.treeControl.dataNodes[i]);
          }
        }
        i -= 1;
      }
    }
  }

}
